import React from 'react';
import { Fade } from 'react-awesome-reveal';
import styles from './BlogSection.module.scss';
import { Typography } from '../Typography';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { Button } from '../Button';
import BlogCard from './BlogCard';
import { formatBlogImageUrl } from '../../utils';

interface BlogSectionProps {
  posts?: any[];
}

// Helper function to format date as "18 Jan 2025"
const formatDate = (dateString: string | Date) => {
  const date = new Date(dateString);
  const day = date.getDate();
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

const BlogSection: React.FC<BlogSectionProps> = ({ posts = [] }) => {
  // Transform API data to component format
  const formatBlogPosts = (apiPosts: any[]) => {
    return apiPosts.map((post) => ({
      id: post._id,
      image: formatBlogImageUrl(post.coverImage?.url),
      category: post.categories?.[0]?.name || 'General',
      readTime: post.readingTime ? `${post.readingTime} min read` : '5 min read',
      title: post.title,
      description: post.summary || 'Read more about this blog post.',
      author: {
        name: 'EPEN Team',
        avatar: '/chat/chat-admin-avatar.png',
        date: post.publishedAt
          ? formatDate(post.publishedAt)
          : formatDate(new Date().toISOString()),
      },
      slug: post.slug,
      body: post.body,
    }));
  };

  const blogPosts = formatBlogPosts(posts);
  const showCarousel = blogPosts.length > 2;

  const carouselSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: true,
    responsive: [
      {
        breakpoint: 1280,
        settings: {
          slidesToShow: 3,
        },
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1.5,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          arrows: false,
        },
      },
    ],
  };

  const handleViewAllPosts = () => {
    window.location.href = '/news';
  };

  return (
    <div className={styles.blogSection}>
      <div className={styles.container}>
        <Fade triggerOnce duration={800} style={{ width: '100%' }}>
          {/* Header */}
          <div className={styles.header}>
            <div className={styles.headerContent}>
              <div className={styles.badge}>
                <img
                  src="/blog-badge.svg"
                  alt="EPEN Ltd. Latest Writings and Blog Posts Badge"
                  width={48}
                  height={48}
                  loading="lazy"
                />
              </div>
              <div className={styles.headerText}>
                <Typography variant="display-md" as="h2" color="primary" className={styles.title}>
                  Latest writings
                </Typography>

                <Typography
                  variant="text-xl"
                  as="p"
                  color="grayDark-400"
                  className={styles.description}
                >
                  The latest news, technologies, and resources from our team.
                </Typography>
              </div>
            </div>

            {/* View All Button in Header */}
            <div className={styles.viewAllButtonInDesktop}>
              <Button
                variant="primary"
                size="md"
                fullWidth
                onClick={handleViewAllPosts}
                label="View all posts"
              />
            </div>
          </div>
          {/* Conditional Rendering */}
          {showCarousel ? (
            // Carousel for Desktop/Tablet (3+ posts)
            <div className={styles.carouselWrapper}>
              <Slider {...carouselSettings} className={styles.carousel}>
                {blogPosts.map((post, index) => (
                  <Fade
                    key={post.id}
                    triggerOnce
                    duration={600}
                    delay={index * 100}
                    style={{ width: '100%' }}
                  >
                    <BlogCard post={post} />
                  </Fade>
                ))}
              </Slider>
            </div>
          ) : (
            // Simple Layout for 2 or less posts (Desktop/Tablet)
            <div className={styles.simpleLayout}>
              {blogPosts.map((post, index) => (
                <Fade key={post.id} triggerOnce duration={600} delay={index * 100}>
                  <BlogCard post={post} />
                </Fade>
              ))}
            </div>
          )}
        </Fade>

        {/* Vertical Layout for Mobile */}
        <div className={styles.mobilePosts}>
          {blogPosts.slice(0, showCarousel ? 2 : blogPosts.length).map((post, index) => (
            <Fade key={post.id} triggerOnce duration={600} delay={index * 100}>
              <BlogCard post={post} />
            </Fade>
          ))}
        </div>

        {/* View All Button in Mobile */}
        <div className={styles.viewAllButtonInMobile}>
          <Button
            variant="primary"
            size="md"
            fullWidth
            onClick={handleViewAllPosts}
            label="View all posts"
          />
        </div>
      </div>
    </div>
  );
};

export default BlogSection;
